import React, { useState } from 'react';
import {
    Button,
    Typography,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Slide,
    Divider,
    Avatar,
    IconButton,
    Alert,
    TableRow,
    TableCell,
    Table,
    TableHead,
    Switch,
    FormControlLabel,
} from '@mui/material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { ArrowBack, Close } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { Connection } from '@domain/connection/models/Connection';
import { ClientType } from '@domain/connection/models/Connector';
import { APP_CONFIG } from '../../../../../config/app.config';
import MontaCredentialsForm from '@features/connections/monta/components/MontaCredentialsForm';
import WarehouseConnectionSelect from '../WarehouseConnectionSelect';
import { MutationType } from '@domain/inventory/enums/MutationType.enum';
import { RuleOperator } from '@domain/inventory/enums/RuleOperator.enum';
import { RuleField } from '@domain/inventory/enums/RuleField.enum';
import { useWarehouses } from '../../../hooks/useWarehouseOperations';
import { useToast } from '@shared/hooks/useToast';
import { WarehouseConnectionType } from '@domain/inventory/enums/WarehouseConnectionType.enum';
import WarehouseDataPreview from '../../warehouse-data-preview/WarehouseDataPreview';
import { AbstractPreviewData } from '../../../services/types';
import { DataTransformationService } from '../../../services/DataTransformationService';
import { MockMontaDataSource } from '../../../services/MockMontaDataSource';

interface MontaWarehouseFormProps {
    onClose: () => void;
    onBack: () => void;
}

const MontaWarehouseForm: React.FC<MontaWarehouseFormProps> = ({
    onClose,
    onBack,
}) => {

    const [form, setForm] = useState({
        name: 'Monta',
        location: '',
    });

    const queryClient = useQueryClient();
    const { error } = useToast();

    const [showCreateConnection, setShowCreateConnection] = useState(false);

    const [syncType, setSyncType] = useState<'all' | 'warehouse' | 'location' | null>(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState<{ id: string, name: string } | null>(null);
    const [selectedLocation, setSelectedLocation] = useState<{ id: string, name: string } | null>(null);

    const [warehouses, setWarehouses] = useState<{ id: string, name: string }[]>([]);
    const [locations, setLocations] = useState<{ id: string, name: string }[]>([]);


    const [showPreview, setShowPreview] = useState(false);
    const [showWarehousePreview, setShowWarehousePreview] = useState(false);
    const [columnMapping, setColumnMapping] = useState<{ ean?: string; quantity?: string }>({});
    const [useMockData, setUseMockData] = useState(false);
    const [selectedSheet, setSelectedSheet] = useState<string>('All Stock');
    const handleCloseCreateConnection = () => {
        setShowCreateConnection(false);
    };

    const handleSyncTypeChange = (type: 'all' | 'warehouse' | 'location') => {
        setSyncType(type);
        setSelectedWarehouse(null);
        setSelectedLocation(null);
        setShowWarehousePreview(true);
    }

    const handleSheetSelect = async (sheetName: string) => {
        setSelectedSheet(sheetName);
        
        if (sheetName === 'All Stock') {
            setSelectedWarehouse(null);
            setSelectedLocation(null);
        } else if (sheetName.startsWith('Warehouse: ')) {
            const warehouseName = sheetName.replace('Warehouse: ', '');
            const warehouse = warehouses.find(w => w.name === warehouseName);
            if (warehouse) {
                setSelectedWarehouse(warehouse);
                setSelectedLocation(null);
            }
        } else if (sheetName.startsWith('Location: ')) {
            const locationName = sheetName.replace('Location: ', '');
            const location = locations.find(l => l.name === locationName);
            if (location) {
                setSelectedLocation(location);
                setSelectedWarehouse(null);
            }
        }
    }


    const handleNewConnection = (marketplaceConnection: any) => {
        setShowCreateConnection(false);
        queryClient.invalidateQueries({ queryKey: ['connections'] });
        setSelectedConnection(marketplaceConnection);
    };

    const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
    const { createWarehouse, isCreating } = useWarehouses();

    const { data: montaProducts } = useQuery({
        queryKey: ['monta-products', useMockData],
        queryFn: async () => {
            let result;
            
            if (useMockData) {
                // Use mock data for testing
                result = await MockMontaDataSource.fetchProducts(selectedConnection?.id || '');
            } else {
                // Use real API
                result = await container.montaConnectorRepository.fetchProducts(selectedConnection?.id || '');
            }
            
            if (result.status === 'error') throw new Error(result.error?.message || 'Unknown error');

            // Set warehouses and locations based on data source
            if (useMockData) {
                // Use mock warehouses and locations for testing
                const mockWarehouses = MockMontaDataSource.generateMockWarehouses();
                const mockLocations = MockMontaDataSource.generateMockLocations();
                console.log('Mock warehouses:', mockWarehouses);
                console.log('Mock locations:', mockLocations);
                setWarehouses(mockWarehouses);
                setLocations(mockLocations);
            } else {
                // Extract unique warehouses and locations from real data
                const warehouses = new Map<string, string>();
                const locations = new Map<string, string>();

                result.data?.forEach(product => {
                    product.Product.Stock.PerWarehouse?.forEach(warehouse => {
                        const guid = warehouse.Warehouse?.Guid;
                        if (guid) warehouses.set(guid, warehouse.Warehouse?.Name || '');
                    });

                    product.Product.Stock.PerLocation?.forEach(location => {
                        const code = location.LocationCode;
                        if (code) locations.set(code, code);
                    });
                });

                setWarehouses(Array.from(warehouses, ([id, name]) => ({ id, name })));
                setLocations(Array.from(locations, ([id, name]) => ({ id, name })));
            }
            
            return result.data;
        },
        enabled: !!selectedConnection?.id
    });

    const handleClose = () => {
        setForm({ name: '', location: '' });
        onClose();
    };

    const handleSubmit = async () => {
        if (!selectedConnection || !syncType || (syncType === 'warehouse' && !selectedWarehouse) || (syncType === 'location' && !selectedLocation)) {
            error('Please select a warehouse or location');
            return;
        }

        if (!columnMapping.ean || !columnMapping.quantity) {
            error('Please map both EAN and Quantity columns');
            return;
        }

        const warehouse = {
            name: form.name,
            location: form.location,
            warehouseConnections: [
                {
                    sourceType: WarehouseConnectionType.MONTA,
                    mutationType: MutationType.SYNC,
                    rules: [
                        {
                            operator: RuleOperator.EQUALS,
                            field: RuleField.CONNECTION_ID,
                            value: selectedConnection?.id
                        },
                        {
                            operator: RuleOperator.EQUALS,
                            field: RuleField.TYPE,
                            value: syncType
                        },
                        {
                            operator: RuleOperator.EQUALS,
                            field: RuleField.EAN_COLUMN_NAME,
                            value: columnMapping.ean
                        },
                        {
                            operator: RuleOperator.EQUALS,
                            field: RuleField.QUANTITY_COLUMN_NAME,
                            value: columnMapping.quantity
                        },
                    ],
                }
            ]
        }

        if (syncType === 'warehouse' && selectedWarehouse) {
            warehouse.warehouseConnections[0].rules.push({
                operator: RuleOperator.EQUALS,
                field: RuleField.WAREHOUSE,
                value: selectedWarehouse.id
            })
        }

        if (syncType === 'location' && selectedLocation) {
            warehouse.warehouseConnections[0].rules.push({
                operator: RuleOperator.EQUALS,
                field: RuleField.LOCATION,
                value: selectedLocation.id
            })
        }

        await createWarehouse(warehouse);

        handleClose();
    };

    const handleCreateConnection = () => {
        setShowCreateConnection(true);
    };

    const { data: connector } = useQuery({
        queryKey: ['monta-connector'],
        queryFn: async () => {
            const result = await container.marketplaceRepository.fetchConnectors();
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data.find(connector => connector.type === 'monta');
        }
    });

    return (
        <>
            <DialogHeader>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center">
                        <IconButton onClick={onBack} color="inherit">
                            <ArrowBack />
                        </IconButton>

                        <Typography variant="h6" component="div">
                            Sync with Monta
                        </Typography>
                    </Stack>
                </Stack>
            </DialogHeader>

            <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
                <Stack gap={3} sx={{ mt: 2 }}>
                    <Box>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <Box sx={{ width: 100, height: 60, borderRadius: 1, border: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border, p: 2 }}>
                                <img src={connector?.image} alt="Monta" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" color="text.primary">
                                    Sync your inventory with Monta
                                </Typography>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Your Monta inventory will be synced with your Inventory every hour.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Divider />

                    <Stack gap={2} direction="column">
                        <Typography variant="body2" color="text.secondary">
                            Warehouse details
                        </Typography>

                        <Stack gap={2} direction="row">
                            <TextField
                                label="Warehouse name"
                                value={form.name}
                                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                                fullWidth
                                size="small"
                            />

                            <TextField
                                label="Location"
                                value={form.location}
                                onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                                fullWidth
                                size="small"
                                placeholder="e.g., Amsterdam, Netherlands"
                            />
                        </Stack>
                    </Stack>


                    <WarehouseConnectionSelect
                        connectorType={ClientType.MONTA}
                        selectedConnection={selectedConnection}
                        onSelect={setSelectedConnection}
                        onCreateConnection={handleCreateConnection}
                        connectorName="Monta"
                    />


                    {selectedConnection && (

                        <Stack gap={2}>
                            <Typography variant="body2" color="text.secondary">
                                What stock do you want to sync?
                            </Typography>


                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button
                                    variant="outlined"
                                    onClick={() => handleSyncTypeChange('all')}
                                    sx={{
                                        borderColor: syncType === 'all' ? 'primary.main' : 'divider',
                                        borderWidth: 1,
                                        borderRadius: 1,
                                        padding: 2,
                                    }}
                                >
                                    <Typography variant="subtitle2" color="text.primary">
                                        All stock
                                    </Typography>
                                </Button>

                                {warehouses.length > 0 && (
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleSyncTypeChange('warehouse')}
                                        sx={{
                                            borderColor: syncType === 'warehouse' ? 'primary.main' : 'divider',
                                            borderWidth: 1,
                                            borderRadius: 1,
                                            padding: 2,
                                        }}
                                    >
                                        <Typography variant="subtitle2" color="text.primary">
                                            A specific warehouse
                                        </Typography>
                                    </Button>
                                )}

                                {locations.length > 0 && (
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleSyncTypeChange('location')}
                                        sx={{
                                            borderColor: syncType === 'location' ? 'primary.main' : 'divider',
                                            borderWidth: 1,
                                            borderRadius: 1,
                                            padding: 2,
                                        }}
                                    >
                                        <Typography variant="subtitle2" color="text.primary">
                                            A specific location
                                        </Typography>
                                    </Button>
                                )}
                            </Stack>



                            {syncType && (
                                <Stack gap={2}>
                                    <Typography variant="body2" color="text.secondary">
                                        Column mapping
                                    </Typography>
                                    
                                    {(!columnMapping.ean || !columnMapping.quantity) ? (
                                        <Button
                                            variant="outlined"
                                            onClick={() => setShowWarehousePreview(true)}
                                            sx={{
                                                borderColor: 'warning.main',
                                                borderWidth: 1,
                                                borderRadius: 1,
                                                padding: 2,
                                                borderStyle: 'dashed',
                                            }}
                                        >
                                            <Typography variant="subtitle2" color="warning.main">
                                                ⚠️ Map EAN and Quantity columns
                                            </Typography>
                                        </Button>
                                    ) : (
                                        <Box sx={{ p: 2, border: 1, borderColor: 'success.main', borderRadius: 1, backgroundColor: 'success.50' }}>
                                            <Typography variant="subtitle2" color="success.main">
                                                ✅ EAN: {columnMapping.ean} | Quantity: {columnMapping.quantity}
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    )}
                </Stack>
            </DialogContent >

            <DialogActions sx={{ p: 3, alignSelf: 'flex-end', justifyContent: 'flex-end', position: 'absolute', bottom: 0, left: 0, right: 0, borderTop: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border }}>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={
                        !selectedConnection || 
                        !columnMapping.ean || 
                        !columnMapping.quantity ||
                        (syncType === 'warehouse' && !selectedWarehouse) ||
                        (syncType === 'location' && !selectedLocation)
                    }
                >
                    Create warehouse
                </Button>
            </DialogActions>

            {/* Create Monta Connection Dialog */}
            <Dialog
                open={showCreateConnection}
                onClose={handleCloseCreateConnection}
                maxWidth="sm"
            >
                <MontaCredentialsForm
                    onNewConnection={handleNewConnection}
                    onClose={handleCloseCreateConnection}
                />
            </Dialog>

            {/* Warehouse Preview Dialog */}
            {montaProducts && (
                <WarehouseDataPreview
                    open={showWarehousePreview}
                    onClose={() => setShowWarehousePreview(false)}
                    previewData={{
                        ...(() => {
                            console.log('Transform inputs:', {
                                montaProducts: montaProducts?.length,
                                syncType,
                                selectedWarehouse,
                                selectedLocation,
                                warehouses,
                                locations
                            });
                            return DataTransformationService.transformMontaDataToAbstract(
                                montaProducts, 
                                syncType, 
                                selectedWarehouse, 
                                selectedLocation,
                                warehouses,
                                locations,
                                selectedSheet
                            );
                        })(),
                        columnMapping
                    }}
                    onColumnMappingChange={(field, columnName) => {
                        setColumnMapping(prev => ({
                            ...prev,
                            [field]: columnName
                        }));
                    }}
                    onHeaderRowSettingsChange={() => {
                        // Not needed for Monta data
                    }}
                    onReprocessWithNewHeaderRow={async () => {
                        // Not needed for Monta data
                    }}
                    onSheetSelect={handleSheetSelect}
                />
            )}
        </>
    );
};

export default MontaWarehouseForm; 